import cx from "classnames";
import { rem } from "polished";
import {
  forwardRef,
  InputHTMLAttributes,
  useCallback,
  useImperativeHandle,
  useRef,
} from "react";
import styled from "styled-components";
import { v4 as uuid } from "uuid";

import { withViewportStyle } from "@/components/editor/shared/with-viewport-style";
import { ColorLabels } from "@/components/editor/utils/use-color";

import { ThemeKind } from "../types";
import useTheme from "../use-theme";

interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onClear?: () => void;
}

const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ClearIcon = () => (
  <svg
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    focusable="false"
    aria-hidden="true"
  >
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M0 12C0 5.373 5.373 0 12 0a12 12 0 0112 12c0 6.627-5.373 12-12 12S0 18.627 0 12zm15.89 2.652a.576.576 0 00-.182-.42L13.488 12l2.22-2.232a.576.576 0 000-.84l-.636-.636a.576.576 0 00-.84 0L12 10.512l-2.232-2.22a.576.576 0 00-.84 0l-.636.636a.576.576 0 000 .84L10.512 12l-2.22 2.232a.576.576 0 000 .84l.636.636a.576.576 0 00.84 0L12 13.488l2.232 2.22a.576.576 0 00.84 0l.636-.636a.576.576 0 00.182-.42z"
      clipRule="evenodd"
    ></path>
  </svg>
);

const InnerSearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ label, value, onChange, onClear, className, ...props }, ref) => {
    const id = useRef(uuid());
    const isEmpty = value.length === 0;
    const innerRef = useRef<HTMLInputElement>(null);
    const { currentTheme } = useTheme();
    useImperativeHandle(ref, () => innerRef.current, []);

    const handleClear = useCallback(() => {
      if (!isEmpty) {
        onClear?.();
      }
    }, [isEmpty, onClear]);

    const handleSearch = useCallback(() => {
      innerRef.current?.focus();
    }, [innerRef]);

    return (
      <div className={cx(className, currentTheme.kind)} data-empty={isEmpty}>
        <label htmlFor={id.current}>{label}</label>
        <SearchInputWrapper>
          <input
            id={id.current}
            ref={innerRef}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            {...props}
            role="searchbox"
          />
          <ClearButton
            type="button"
            onClick={handleClear}
            aria-label="Clear search"
            disabled={isEmpty}
          >
            <ClearIcon />
          </ClearButton>
          <SearchButton
            type="button"
            aria-label="Search"
            onClick={handleSearch}
          >
            <SearchIcon />
          </SearchButton>
        </SearchInputWrapper>
      </div>
    );
  }
);

InnerSearchInput.displayName = "SearchInput";

export const SearchInputWrapper = styled.div`
  display: flex;
  align-items: stretch;
  position: relative;
  background-color: ${({ theme }) => theme.colors[ColorLabels.SECONDARY]};
  color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
`;

export const SearchInput = withViewportStyle(InnerSearchInput)`
  position: relative;
  
  ${(props) => props.$viewport.mobile`
    margin-bottom: ${rem(8)};
  `}

  label, input {
     font-family: ${({ theme }) =>
       theme.fontImports[0]?.font?.name ?? theme.fonts[0].value};
  }

  label {
    position: absolute;
    display:block;
    color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY_FONT]};
    
    left: 0;
    top: -${rem(24)};
    margin-bottom: ${rem(8)};
    font-size: ${rem(12)};
    line-height: ${rem(16)};
    font-weight: 500;

    ${(props) => props.$viewport.mobile`
      position: static;
    `}    
  }

  &.${ThemeKind.NOLAN} label {
    color: ${({ theme }) => theme.colors[ColorLabels.SECONDARY_FONT]};
  }

  &.${ThemeKind.CROSBY} label {
    color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY_FONT]};
  }  

  input {
    background-color: ${({ theme }) => theme.colors[ColorLabels.SECONDARY]};
    color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
    width: 100%;
    height: ${rem(32)};
    padding-right: ${rem(72)};
    padding-left: ${rem(12)};

    &:focus {
      border: none;
      transition-duration: 0s;
      outline: ${rem(2)} solid ${({ theme }) =>
  theme.colors[ColorLabels.PRIMARY]};
    }
  }

  &.${ThemeKind.NOLAN} input {
    color: ${({ theme }) => theme.colors[ColorLabels.BACKGROUND]};
    background-color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
  }

  button { 
    position: absolute;
    height: 100%;
    width: ${rem(32)};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: ${rem(8)};
  }
`;

export const ClearButton = styled.button`
  color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
  right: 2.375rem;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.25;
  }

  .${ThemeKind.NOLAN} & {
    color: ${({ theme }) => theme.colors[ColorLabels.BACKGROUND]};
  }
`;

export const SearchButton = styled.button`
  color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
  right: 0.25rem;

  .${ThemeKind.NOLAN} & {
    color: ${({ theme }) => theme.colors[ColorLabels.BACKGROUND]};
  }
`;
